'use client';

/* eslint-disable tailwindcss/no-arbitrary-value -- text-[--text-secondary]: the text/secondary theme key is shadowed by the legacy preset (see fe CLAUDE.md, Design Tokens) */
import classNames from 'classnames';
import { useId, useState } from 'react';
import { CmsOffer } from '../../../../../../types/cmsProduct';
import { useCmsClientContext } from '../../../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../../../utils/translations/tr';
import { AsideModal, useAsideModalClose } from '../../../../../AsideModal';
import { MaterialIcon } from '../../../../../MaterialIcon';
import { RichText } from '../../../../../RichText/index';
import { GrayInfoBlock } from '../../../../BaseComponents/GrayInfoBlock';
import { ProductDetailsSectionWrapper } from '../../../../BaseComponents/ProductDetailsSectionWrapper';
import { FormattedOfferReward } from '../../formatOfferReward';
import { OffersPlText, TRANSLATIONS } from '../../i18n';
import { OfferGiftSet } from './utils/getGiftSets';

export type ProductDetailsSectionOffersPlAsideModalEntry = {
  key: string;
  title: string;
  type: CmsOffer['type'];
  reward: FormattedOfferReward;
  introduction?: string;
  description?: string;
  giftSets: OfferGiftSet[];
};

export type ProductDetailsSectionOffersPlAsideModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entries: ProductDetailsSectionOffersPlAsideModalEntry[];
};

// Offer details panel of the PL Offers section (CMS-2023 §4): slides in from
// the right; Back, ✕, the scrim and Escape close it. One offer entry is open
// at a time; Terms and conditions is a nested toggle inside each entry.
export const ProductDetailsSectionOffersPlAsideModal: React.FC<
  ProductDetailsSectionOffersPlAsideModalProps
> = ({ isOpen, onClose, entries }) => (
  <AsideModal
    isOpen={isOpen}
    onClose={onClose}
    size="small"
    // The panel has its own header with Back and ✕.
    buttonCloseClassNames="!hidden"
    backgroundClassNames="!bg-fill/base"
  >
    <PanelContent entries={entries} />
  </AsideModal>
);

const PanelContent: React.FC<{
  entries: ProductDetailsSectionOffersPlAsideModalEntry[];
}> = ({ entries }) => {
  // Closes through the drawer so the slide-out transition plays.
  const close = useAsideModalClose();
  const context = useCmsClientContext();
  const text = getClientTranslations(TRANSLATIONS, context);
  // The first offer that rewards at the selection starts open.
  const [openKey, setOpenKey] = useState<string | undefined>(
    () =>
      (entries.find((entry) => entry.reward.kind !== 'none') ?? entries[0])
        ?.key,
  );

  return (
    <div className="flex min-h-screen flex-col">
      <div className="sticky top-0 z-10 flex items-center gap-spacing/gap/x-sm border-b border-borders/default bg-fill/base px-spacing/padding/x py-spacing/padding/y-sm">
        <button
          type="button"
          onClick={close}
          className="flex flex-1 items-center gap-spacing/gap/x-sm text-left mhg-alpha-typography-subheading-2 text-text/link"
        >
          <MaterialIcon icon="arrow_back" px={22} />
          {tr('back_to_loan_details', text)}
        </button>
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="flex text-text/primary"
        >
          <MaterialIcon icon="close" px={22} />
        </button>
      </div>
      <div className="px-spacing/padding/x py-spacing/padding/section-y">
        <ProductDetailsSectionWrapper>
          <div className="flex flex-col gap-spacing/gap/y-sm px-spacing/padding/x py-spacing/padding/y">
            {entries.map((entry) => (
              <OfferEntry
                key={entry.key}
                entry={entry}
                isOpen={openKey === entry.key}
                onToggle={() =>
                  setOpenKey((current) =>
                    current === entry.key ? undefined : entry.key,
                  )
                }
                text={text}
              />
            ))}
          </div>
        </ProductDetailsSectionWrapper>
      </div>
    </div>
  );
};

const OfferEntry: React.FC<{
  entry: ProductDetailsSectionOffersPlAsideModalEntry;
  isOpen: boolean;
  onToggle: () => void;
  text: OffersPlText;
}> = ({ entry, isOpen, onToggle, text }) => {
  const panelId = useId();
  const { reward } = entry;

  return (
    <div className="flex flex-col gap-spacing/gap/y-sm">
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className={classNames(
            'flex w-full items-start gap-spacing/gap/soft-4px rounded-radius/card-inner px-button/padding-regular-x py-form/inner-element-padding text-left',
            entry.type === 'welcome'
              ? 'bg-highlights/orange-light text-highlights/orange-dark'
              : 'bg-fill/primary-light text-fill/primary',
          )}
        >
          <span className="flex flex-1 flex-col items-start gap-spacing/gap/soft-4px">
            <span className="mhg-alpha-typography-body-2-relaxed">
              {entry.title}
            </span>
            {reward.kind === 'none' ? (
              // Where the reward pill sits, so it reads without expanding.
              <span className="mhg-alpha-typography-body-2-relaxed text-text/secondary-lighter">
                {tr('not_available', text)}
              </span>
            ) : (
              <span className="rounded-radius/card border border-current px-button/padding-small-x py-tag/padding-icon mhg-alpha-typography-badge font-bold">
                {reward.kind === 'value'
                  ? tr('up_to_value', text, { value: reward.value })
                  : tr('see_details', text)}
              </span>
            )}
          </span>
          <MaterialIcon
            icon="expand_more"
            px={16}
            className={classNames('transition-transform', {
              'rotate-180': isOpen,
            })}
          />
        </button>
      </h3>
      {isOpen && (
        <div id={panelId} className="flex flex-col gap-spacing/gap/y-sm">
          {entry.introduction && (
            <div className="mhg-alpha-typography-body-2-relaxed text-[--text-secondary]">
              {/* Same text style as the gift list; RichText defaults to body-1. */}
              <RichText baseTextStyle="Body2Relaxed">
                {entry.introduction}
              </RichText>
            </div>
          )}
          {reward.kind !== 'none' &&
            entry.giftSets.map((set) => (
              <div key={set.key} className="flex flex-col gap-spacing/gap/y-xs">
                {set.chooseOneOf && (
                  <p className="m-0 mhg-alpha-typography-caption text-text/secondary-lighter">
                    {tr('choose_one_of', text, {
                      count: String(set.chooseOneOf),
                    })}
                  </p>
                )}
                <ul className="m-0 flex list-disc flex-col gap-spacing/gap/y-xs pl-spacing/padding/y mhg-alpha-typography-body-2-relaxed text-text/primary">
                  {set.gifts.map((gift) => (
                    <li key={gift.key}>
                      {gift.title}
                      {gift.worth && (
                        <span className="text-text/secondary-lighter">
                          {' · '}
                          {tr('worth_value', text, { value: gift.worth })}
                        </span>
                      )}
                      {gift.topup && (
                        <span className="block mhg-alpha-typography-caption text-text/secondary-lighter">
                          {gift.topup}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          {entry.description && (
            <GrayInfoBlock
              title={tr('terms_and_conditions', text)}
              titleClassName="mhg-alpha-typography-body-2-relaxed"
              // Nested under the offer entry's h3.
              headingLevel="h4"
            >
              <RichText>{entry.description}</RichText>
            </GrayInfoBlock>
          )}
        </div>
      )}
    </div>
  );
};
